import DialogContentText from '@mui/material/DialogContentText';
import React from 'react';
import { DialogHandlerProps } from 'hooks/useDialogHandler';
import { ProgressHandler } from 'hooks/useProgressHandler';
import { companyUrls } from 'urls';
import { Goal, toUnbornGoalDTO, UnbornGoal } from 'types/goals';
import ProgressConfirmDialog from 'ui/modules/dialogs/ProgressConfirmDialog';
import useNotify from 'hooks/useNotify';
import useRoute from 'hooks/useRoute';
import { ICompany } from 'types/company';
import GoalsAPI from 'apis/GoalsAPI';
import useLazyResource from 'util/resource/useLazyResource';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';

interface Props {
  company: ICompany;
  goal: Goal;
  dialogHandler: DialogHandlerProps;
  onFinishGoalAction: () => void;
}

export default function CompleteGoalDialog(props: Props) {
  const { company, goal } = props;
  const { dialogHandler, onFinishGoalAction } = props;
  const { push } = useRoute();
  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(company.id);

  const goalsApi = GoalsAPI.goal(company.id);
  const [editGoal] = useLazyResource((dto: UnbornGoal) => goalsApi.edit(goal.id, dto), {
    onSuccess: () => {
      notify('success', 'Updated goal');
      setLastUpdated(new Date());
      push(companyUrls.overview(company.slug, 'goals'));
    },
    onFailure: error => notify('error', error || 'Could not complete goal'),
  });

  const onConfirm = async (progressHandler: ProgressHandler) => {
    const dto: UnbornGoal = { ...toUnbornGoalDTO(goal), state: 'completed' };
    progressHandler.onProgress();
    await editGoal(dto);
    progressHandler.onCleanup('success');
    props.onFinishGoalAction();
  };

  return (
    <ProgressConfirmDialog
      maxWidth="sm"
      title="Complete goal"
      dismissLabel="Cancel"
      confirmLabel="Mark complete"
      confirmColor="blue"
      dialogHandler={dialogHandler}
      onClose={onFinishGoalAction}
      onConfirm={onConfirm}
    >
      <DialogContentText>If you have completed this goal, it may be marked as completed.</DialogContentText>
    </ProgressConfirmDialog>
  );
}
